import { Resource } from 'modules/core/base-resource';
import { Roles } from 'modules/roles-manager/domain/interfaces/role';
import { User, UserDto } from 'modules/roles-manager/domain/interfaces/user';
import { mapUserResponseDto } from 'modules/roles-manager/domain/data-mappers/user.data-mapper';
import playersResource from 'modules/teams/domain/resources/players-resource';
import httpResource from 'modules/core/http-resource';

export type UpdateUserPhotoPayload = { photo: File };
export type GetUsersPayload = {
  limit?: number;
  offset?: number;
  name?: string;
  surname?: string;
  middleName?: string;
  phone?: string;
  isActive?: number;
};

export type CreateUserPayload = Pick<User, 'name' | 'surname' | 'birthDate' | 'middleName' | 'email' | 'phone'> & { role: Roles };
export type UpdateUserPayload = Pick<User, 'id' | 'name' | 'surname' | 'birthDate' | 'middleName' | 'isActive'> & {
  role: Roles;
} & Partial<UpdateUserPhotoPayload>;
export type DeleteUserPayload = Pick<User, 'id'>;

class RoleUsers extends Resource {
  constructor() {
    super('tournaments', httpResource);
  }

  public async getUsers(tournamentId: number, params: GetUsersPayload): Promise<User[]> {
    const result = await this.child(`${tournamentId}/users`).get<UserDto[]>(params);
    return mapUserResponseDto(result);
  }

  public async createUser(tournamentId: number, user: CreateUserPayload): Promise<User> {
    const result = await this.child(`${tournamentId}/users`).post<UserDto>(user);
    return mapUserResponseDto([result])[0];
  }

  public async updateUser(tournamentId: number, user: UpdateUserPayload): Promise<User> {
    const { id, birthDate, ...rest } = user;
    let result = await this.child(`${tournamentId}/users/${id}`).put<UserDto>({
      ...rest,
      birthDate: birthDate ?? new Date(0).toISOString(),
    } as Omit<UpdateUserPayload, 'id'>);

    if (user.photo) {
      const photo = Array.isArray(user.photo) ? user.photo[0] : user.photo;
      if (photo instanceof File) {
        result = await playersResource.uploadPhoto(photo, user.id, tournamentId);
      } else throw new Error('Invalid photo type');
    }

    return mapUserResponseDto([result])[0];
  }

  public async deleteUser(tournamentId: number, params: DeleteUserPayload): Promise<boolean> {
    const { id: userId } = params;
    const response = await this.child(`${tournamentId}/users/${userId}`).delete<{ result: string }>();
    return response.result === 'ok';
  }
}

const roleUsers = new RoleUsers();
export default roleUsers;
